import { useEffect, useState } from "react";
import { Building2, Mail, Phone, Globe, MapPin, User, FileText, Calendar, Database, Shield, Calculator, Copy, Blocks } from "lucide-react";
import { EMPLOYER } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import { Button } from "../../components/ui/button";
import ToastMsg from "../../components/common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";

const CompanyProfilePage = () => {
    const { fetch } = useApiMethods();
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(false);

    const fetchCompanyDetails = async () => {
        setLoading(true);
        try {
            const response = await fetch(EMPLOYER);
            setData(response.data);
        } catch (error) {
            console.error(error);
            ToastMsg("error", "Failed to load company details");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCompanyDetails();
    }, []);

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        ToastMsg("success", "Copied to clipboard");
    };

    const InfoItem = ({ icon: Icon, label, value, copyable = false }) => (
        <div className="flex items-start justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
            <div className="flex items-start gap-3 flex-1">
                <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg mt-0.5">
                    <Icon className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        {label}
                    </span>
                    <p className="text-sm text-gray-900 dark:text-white mt-1 truncate">
                        {value || "-"}
                    </p>
                </div>
            </div>
            {copyable && value && (
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(value)}
                    className="p-1 h-6 w-6 ml-2"
                >
                    <Copy className="w-3 h-3" />
                </Button>
            )}
        </div>
    );

    const SectionCard = ({ title, icon: Icon, children, className }) => (
        <div className={cn(
            "bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700",
            className
        )}>
            <div className="flex items-center gap-2 p-4 border-b border-gray-100 dark:border-gray-700">
                <Icon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                <h3 className="text-sm font-semibold text-sidebar dark:text-white uppercase tracking-wide">
                    {title}
                </h3>
            </div>
            <div className="p-4">
                {children}
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="h-[calc(100vh-100px)] dark:bg-gray-900 flex items-center justify-center rounded-md">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary"></div>
            </div>
        );
    }

    return (
        <ContainerCard scrollable>
            {/* Header */}
            <ModuleTitleCard Icon={Blocks} title={'Organization'} subtitle="Organization information and system configuration" />

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Left Column - Company Overview */}
                <div className="lg:col-span-2 space-y-4">
                    {/* Company Profile Header with Image */}
                    <SectionCard title="Company Profile" icon={Building2}>
                        <div className="flex flex-col sm:flex-row gap-8 items-start sm:items-center">
                            {/* Profile Image */}
                            <div className="flex-shrink-0">
                                <div className="relative group">
                                    {data.profile ? (
                                        <img
                                            src={data.profile}
                                            alt="Company Logo"
                                            className="w-40 h-40 rounded-lg object-cover border-2 border-gray-200 dark:border-gray-600"
                                            onError={(e) => {
                                                e.target.src = "/default-company.png";
                                            }}
                                        />
                                    ) : (
                                        <div className={`w-40 h-40 rounded-lg border-2 border-gray-200 dark:border-gray-600 bg-gradient-to-br from-sidebar to-sidebar-primary dark:from-blue-900 dark:to-blue-800 flex items-center justify-center ${data.profile ? 'hidden' : 'flex'}`}>
                                            <span className="text-4xl font-bold text-white dark:text-blue-300">
                                                {data.company_name ? data.company_name.charAt(0).toUpperCase() : 'C'}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Company Basic Info */}
                            <div className="flex-1 min-w-0">
                                <div className="space-y-1">
                                    <InfoItem
                                        icon={Building2}
                                        label="Company Name"
                                        value={data.company_name}
                                        copyable
                                    />
                                    <InfoItem
                                        icon={FileText}
                                        label="Company Prefix"
                                        value={data.prefix}
                                    />
                                    <InfoItem
                                        icon={Mail}
                                        label="Official Email"
                                        value={data.official_email_address}
                                        copyable
                                    />
                                </div>
                            </div>
                        </div>
                    </SectionCard>

                    {/* Company Details */}
                    <SectionCard title="Company Information" icon={FileText}>
                        <div className="space-y-1">
                            <InfoItem
                                icon={Phone}
                                label="Landline"
                                value={data.landline}
                                copyable
                            />
                            <InfoItem
                                icon={Globe}
                                label="Website"
                                value={data.website}
                                copyable
                            />
                            <InfoItem
                                icon={Shield}
                                label="PAN Number"
                                value={data.pan_no}
                                copyable={data.pan_no ? true : false}
                            />
                            <InfoItem
                                icon={Shield}
                                label="GST Number"
                                value={data.gst_number}
                                copyable={data.gst_number ? true : false}
                            />
                            <InfoItem
                                icon={Building2}
                                label="Business Type"
                                value={data.business_type}
                            />
                            <InfoItem
                                icon={FileText}
                                label="Registration Number"
                                value={data.registration_number}
                            />
                        </div>
                    </SectionCard>

                    {/* Registered Address */}
                    <SectionCard title="Registered Address" icon={MapPin}>
                        <div className="space-y-1">
                            <InfoItem
                                icon={MapPin}
                                label="Full Address"
                                value={data.registered_address}
                            />
                            <div className="grid grid-cols-2 gap-4">
                                <InfoItem
                                    icon={MapPin}
                                    label="City"
                                    value={data.city}
                                />
                                <InfoItem
                                    icon={MapPin}
                                    label="State"
                                    value={data.state}
                                />
                                <InfoItem
                                    icon={MapPin}
                                    label="Country"
                                    value={data.country}
                                />
                                <InfoItem
                                    icon={MapPin}
                                    label="Pincode"
                                    value={data.pincode}
                                />
                            </div>
                        </div>
                    </SectionCard>
                </div>

                {/* Right Column - Additional Information */}
                <div className="space-y-4">
                    {/* Contact Person */}
                    <SectionCard title="Contact Person" icon={User}>
                        <div className="space-y-1">
                            <InfoItem
                                icon={User}
                                label="Contact Person"
                                value={data.contact_person}
                            />
                            <InfoItem
                                icon={Phone}
                                label="Contact Number"
                                value={data.contact_person_number}
                                copyable
                            />
                        </div>
                    </SectionCard>

                    {/* System Configuration */}
                    <SectionCard title="System Settings" icon={Database}>
                        <div className="space-y-1">
                            <InfoItem
                                icon={Database}
                                label="Database Name"
                                value={data.db_name}
                            />
                            <InfoItem
                                icon={Calculator}
                                label="Salary Calculation"
                                value={data.salary_calculation}
                            />
                            <InfoItem
                                icon={Calendar}
                                label="Financial Year"
                                value={data.is_financial_year ? "Calendar Year" : "Fiscal Year"}
                            />
                            <InfoItem
                                icon={Shield}
                                label="Asset Auto Generation"
                                value={data.is_asset_auto_gen ? "Enabled" : "Disabled"}
                            />
                            <InfoItem
                                icon={Calendar}
                                label="Check-in Grace Time"
                                value={data.checkin_grace_time ? `${data.checkin_grace_time} minutes` : "Not set"}
                            />
                        </div>
                    </SectionCard>

                    {/* Company Status */}
                    <SectionCard title="Company Status" icon={Shield}>
                        <div className="space-y-1">
                            <InfoItem
                                icon={Calendar}
                                label="Created Date"
                                value={data.created_date ? new Date(data.created_date).toLocaleDateString() : "-"}
                            />
                            <InfoItem
                                icon={Calendar}
                                label="Last Modified"
                                value={data.modified_date ? new Date(data.modified_date).toLocaleDateString() : "-"}
                            />
                            <InfoItem
                                icon={Shield}
                                label="Status"
                                value={data.is_active ? "Active" : "Inactive"}
                            />
                            <InfoItem
                                icon={FileText}
                                label="Document Status"
                                value={data.is_draft ? "Draft" : "Published"}
                            />
                        </div>
                    </SectionCard>
                </div>
            </div>
        </ContainerCard>
    );
};

export default CompanyProfilePage;